import Box from '@mui/material/Box';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import type { AdPlacement } from '@openx/types/adUnit.ts';
import type { DistributionChannel } from '@openx/types/distributionChannel.ts';
import { type FormatTypeData, InventoryFormatTypeOption } from '@openx/types/targeting/formatType.ts';
import { Criteria, Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext.tsx';
import { DimensionChipLine } from '../shared/DimensionChip/DimensionChipLine.tsx';
import { EmptyFilterMessage } from '../shared/EmptyFilterMessage/EmptyFilterMessage.tsx';
import { FiltersTitle } from '../shared/FiltersTitle/FiltersTitle.tsx';
import {
  TargetingDropdownSection,
  type TargetingDropdownSectionProps,
} from '../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection.tsx';
import { SelectedItems } from '../shared/SelectedItems/SelectedItems.tsx';

export interface FormatTypeItemsProps {
  targetingParams: FormatTypeData | null;
  readonly?: boolean;
  setDistributionChannel?: Dispatch<SetStateAction<DistributionChannel | null>>;
  setAdPlacement?: Dispatch<SetStateAction<AdPlacement | null>>;
  updateParams?: Dispatch<SetStateAction<FormatTypeData | null>>;
}

export const FormPreviewItems = ({
  targetingParams,
  readonly = true,
  onOpenDrawerClick,
  onRemove,
  onRemoveParameter,
  optionsCount,
  disabledReasonMessage,
}: FormatTypeItemsProps &
  TargetingDropdownSectionProps & { onRemoveParameter?: (opt: InventoryFormatTypeOption) => void }) => {
  const { t } = useTranslation();
  const { useAdPlacementOptions, useDistributionChannelOptions } = useTargetingContext();
  const { isAdPlacementLoading, adPlacement: adPlacementMap } = useAdPlacementOptions();
  const { isDistributionChannelLoading, distributionChannel: distributionChannelMap } = useDistributionChannelOptions();

  const distributionChannelSwitchState =
    targetingParams?.[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL]?.op === Intersect.NOT_INTERSECTS;
  const adPlacementSwitchState =
    targetingParams?.[InventoryFormatTypeOption.AD_PLACEMENT]?.op === Intersect.NOT_INTERSECTS;

  const distributionChannel = targetingParams?.[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL]?.val;
  const adPlacement = targetingParams?.[InventoryFormatTypeOption.AD_PLACEMENT]?.val;

  const showChip = !!(adPlacement?.length && distributionChannel?.length);

  const distributionChannelFilterTitle = t('Distribution Channel [ {count} ]', {
    count: distributionChannel?.split(',').length,
  });
  const adPlacementFilterTitle = t('Ad Placement [ {count} ]', { count: adPlacement?.split(',').length });

  if (isAdPlacementLoading || isDistributionChannelLoading) {
    return <Loader />;
  }

  if (!adPlacement && !distributionChannel) {
    return <EmptyFilterMessage text={t('No filters applied.')} />;
  }

  return (
    <Box data-test="sub-section">
      {!!(targetingParams && distributionChannel?.length) && (
        <TargetingDropdownSection
          name={t('Distribution Channel')}
          isEmpty={false}
          onOpenDrawerClick={onOpenDrawerClick}
          onRemove={
            onRemoveParameter ? () => onRemoveParameter(InventoryFormatTypeOption.DISTRIBUTION_CHANNEL) : onRemove
          }
          hasOptionsSelected={!!optionsCount && optionsCount > 0}
        >
          <FiltersTitle
            title={distributionChannelFilterTitle}
            readonly={readonly}
            disabledReasonMessage={disabledReasonMessage}
          />

          <SelectedItems
            filtersTitle={distributionChannelSwitchState ? t('excluded') : t('included')}
            isAddIcon={!distributionChannelSwitchState}
            selectedItems={distributionChannel?.replaceAll(' ', '').split(',')}
            readonly={readonly}
            options={distributionChannelMap}
            dataTest="distribution-channel"
          />
        </TargetingDropdownSection>
      )}

      {showChip && <DimensionChipLine targetingDimension={targetingParams?.op ?? Criteria.ALL} />}

      {!!(targetingParams && adPlacement?.length) && (
        <TargetingDropdownSection
          name={t('Ad Placement')}
          isEmpty={false}
          onOpenDrawerClick={onOpenDrawerClick}
          onRemove={onRemoveParameter ? () => onRemoveParameter(InventoryFormatTypeOption.AD_PLACEMENT) : onRemove}
          hasOptionsSelected={!!optionsCount && optionsCount > 0}
        >
          <FiltersTitle
            title={adPlacementFilterTitle}
            readonly={readonly}
            disabledReasonMessage={disabledReasonMessage}
          />

          <SelectedItems
            filtersTitle={adPlacementSwitchState ? t('excluded') : t('included')}
            isAddIcon={!adPlacementSwitchState}
            selectedItems={adPlacement?.replaceAll(' ', '').split(',')}
            readonly={readonly}
            options={adPlacementMap}
            dataTest="ad-placement"
          />
        </TargetingDropdownSection>
      )}
    </Box>
  );
};
